import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChipIcon } from './ChipIcon.js';
import { api } from '../lib/api.js';
import { useToast } from '../stores/toast.js';
import { useAuth } from '../stores/auth.js';
import { haptic, hapticSuccess } from '../lib/telegram.js';

interface WithdrawSheetProps {
  open: boolean;
  onClose: () => void;
}

type Tab = 'cryptobot' | 'ton';

const CRYPTOBOT_ASSETS = [
  { code: 'USDT', label: 'USDT' },
  { code: 'TON', label: 'TON' },
  { code: 'BTC', label: 'BTC' },
  { code: 'ETH', label: 'ETH' },
] as const;

type AssetCode = (typeof CRYPTOBOT_ASSETS)[number]['code'];

const MIN_CRYPTOBOT_STARS = 50;
const MIN_TON_STARS = 200;

export function WithdrawSheet({ open, onClose }: WithdrawSheetProps) {
  const user = useAuth((s) => s.user);
  const refreshUser = useAuth((s) => s.refresh);
  const pushToast = useToast((s) => s.push);
  const [tab, setTab] = useState<Tab>('cryptobot');

  const [asset, setAsset] = useState<AssetCode>('USDT');
  const [stars, setStars] = useState<string>('');

  const [tonAddress, setTonAddress] = useState<string>('');
  const [tonStars, setTonStars] = useState<string>('');

  const [busy, setBusy] = useState(false);

  const balance = user?.starsBalance ?? 0;

  const cbStarsAmount = (() => {
    const n = Number.parseInt(stars, 10);
    return Number.isFinite(n) && n >= MIN_CRYPTOBOT_STARS && n <= balance ? n : null;
  })();

  const tonStarsAmount = (() => {
    const n = Number.parseInt(tonStars, 10);
    return Number.isFinite(n) && n >= MIN_TON_STARS && n <= balance ? n : null;
  })();

  const submitCryptoBot = async (): Promise<void> => {
    if (cbStarsAmount === null) return;
    haptic('medium');
    setBusy(true);
    try {
      const res = await api.cryptoBotWithdraw(asset, cbStarsAmount);
      hapticSuccess();
      pushToast(`Отправлено ${res.assetAmount} ${asset} в @CryptoBot`, 'success');
      await refreshUser();
      setStars('');
      onClose();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Не удалось вывести', 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitTon = async (): Promise<void> => {
    if (tonStarsAmount === null || tonAddress.trim().length < 10) return;
    haptic('medium');
    setBusy(true);
    try {
      const res = await api.tonWithdraw(tonAddress.trim(), tonStarsAmount);
      hapticSuccess();
      pushToast(`Отправлено ${res.amountTon} TON`, 'success');
      await refreshUser();
      setTonStars('');
      setTonAddress('');
      onClose();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Не удалось вывести TON', 'error');
    } finally {
      setBusy(false);
    }
  };

  const setMaxFor = (kind: Tab): void => {
    if (kind === 'cryptobot') setStars(balance.toString());
    else setTonStars(balance.toString());
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0, 0, 0, 0.55)' }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-[480px] flex flex-col gap-4 px-5 pb-6 pt-3"
            style={{
              background:
                'linear-gradient(180deg, color-mix(in oklab, var(--color-felt-deep) 95%, white 4%) 0%, var(--color-felt-deep) 100%)',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              borderTop: '1px solid color-mix(in oklab, var(--color-gold) 35%, transparent)',
              boxShadow: '0 -20px 40px -20px rgba(0,0,0,0.7)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            }}
          >
            <div className="mx-auto h-1 w-10 rounded-full bg-ivory/25" />

            <div className="flex flex-col gap-1">
              <h2 className="heading-display text-2xl">Вывести</h2>
              <p className="text-xs text-ivory/55">
                Фишки конвертируются в крипту с небольшим спредом — он покрывает on-chain комиссии.{' '}
                <Link to="/balance" onClick={onClose} className="text-gold underline">
                  Подробнее
                </Link>
              </p>
            </div>

            <div className="flex items-center justify-between text-sm text-ivory/70">
              <span>Доступно</span>
              <span className="flex items-center gap-1.5 text-gold" style={{ fontFamily: 'var(--font-display)' }}>
                {balance.toLocaleString('ru-RU')} <ChipIcon size={14} />
              </span>
            </div>

            <div
              className="flex rounded-full p-1"
              style={{
                background: 'var(--color-glass)',
                border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
              }}
            >
              {(['cryptobot', 'ton'] as const).map((id) => (
                <button
                  key={id}
                  onClick={() => {
                    haptic('light');
                    setTab(id);
                  }}
                  className="flex-1 rounded-full py-2 text-xs transition-colors"
                  style={{
                    background: tab === id ? 'var(--color-gold)' : 'transparent',
                    color: tab === id ? 'var(--color-felt-deep)' : 'var(--color-ivory)',
                    fontWeight: tab === id ? 700 : 500,
                  }}
                >
                  {id === 'cryptobot' ? 'В @CryptoBot' : 'На TON-адрес'}
                </button>
              ))}
            </div>

            {tab === 'cryptobot' && (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {CRYPTOBOT_ASSETS.map((a) => (
                    <button
                      key={a.code}
                      onClick={() => {
                        haptic('light');
                        setAsset(a.code);
                      }}
                      className="rounded-full px-3 py-1.5 text-xs transition-colors"
                      style={{
                        background: asset === a.code ? 'var(--color-gold)' : 'var(--color-glass)',
                        color: asset === a.code ? 'var(--color-felt-deep)' : 'var(--color-ivory)',
                        border: '1px solid color-mix(in oklab, var(--color-gold) 40%, transparent)',
                        fontFamily: 'var(--font-display)',
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs uppercase tracking-wider text-ivory/55">Фишек</label>
                    <button
                      onClick={() => setMaxFor('cryptobot')}
                      disabled={balance < MIN_CRYPTOBOT_STARS}
                      className="text-xs text-gold underline-offset-2 hover:underline disabled:opacity-40"
                    >
                      Всё
                    </button>
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={MIN_CRYPTOBOT_STARS}
                    max={balance}
                    value={stars}
                    onChange={(e) => setStars(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder={`мин. ${MIN_CRYPTOBOT_STARS}`}
                    className="rounded-2xl px-4 py-3 text-base outline-none"
                    style={{
                      background: 'var(--color-glass)',
                      border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
                      color: 'var(--color-ivory)',
                      fontFamily: 'var(--font-display)',
                    }}
                  />
                  {stars.length > 0 && cbStarsAmount === null && (
                    <p className="text-[11px] text-coral/85">
                      Сумма от {MIN_CRYPTOBOT_STARS} до {balance.toLocaleString("ru-RU")} фишек
                    </p>
                  )}
                </div>

                <button
                  disabled={busy || cbStarsAmount === null}
                  onClick={() => void submitCryptoBot()}
                  className="btn-primary"
                >
                  {busy ? 'Отправляем…' : `Вывести в ${asset}`}
                </button>
              </>
            )}

            {tab === 'ton' && (
              <>
                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-wider text-ivory/55">TON-адрес получателя</label>
                  <input
                    type="text"
                    value={tonAddress}
                    onChange={(e) => setTonAddress(e.target.value)}
                    placeholder="EQ…"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className="rounded-2xl px-4 py-3 text-sm outline-none"
                    style={{
                      background: 'var(--color-glass)',
                      border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
                      color: 'var(--color-ivory)',
                      fontFamily: 'var(--font-display)',
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs uppercase tracking-wider text-ivory/55">Фишек</label>
                    <button
                      onClick={() => setMaxFor('ton')}
                      disabled={balance < MIN_TON_STARS}
                      className="text-xs text-gold underline-offset-2 hover:underline disabled:opacity-40"
                    >
                      Всё
                    </button>
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={MIN_TON_STARS}
                    max={balance}
                    value={tonStars}
                    onChange={(e) => setTonStars(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder={`мин. ${MIN_TON_STARS}`}
                    className="rounded-2xl px-4 py-3 text-base outline-none"
                    style={{
                      background: 'var(--color-glass)',
                      border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
                      color: 'var(--color-ivory)',
                      fontFamily: 'var(--font-display)',
                    }}
                  />
                  {tonStars.length > 0 && tonStarsAmount === null && (
                    <p className="text-[11px] text-coral/85">
                      Сумма от {MIN_TON_STARS} до {balance.toLocaleString("ru-RU")} фишек
                    </p>
                  )}
                </div>

                <button
                  disabled={busy || tonStarsAmount === null || tonAddress.trim().length < 10}
                  onClick={() => void submitTon()}
                  className="btn-primary"
                >
                  {busy ? 'Отправляем…' : 'Вывести TON'}
                </button>
              </>
            )}

            <button
              onClick={onClose}
              className="text-center text-sm text-ivory/55 underline-offset-2 hover:underline"
            >
              Отмена
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
